"use client"
import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Label } from "@/components/ui/label"
import { NumericInput } from "@/components/ui/numeric-input"
import { integerCodec } from "@/lib/forms/numeric-draft"
import { cn } from "@/lib/utils"
import { AlertTriangle } from "lucide-react"

/*
  `text-base` (16px) is required to stop iOS from zooming into the input
  on focus; anything below 16px triggers the auto-zoom heuristic.
*/
const nativeInputClass =
  "w-full min-w-0 appearance-none text-base rounded-xl px-3 py-3 bg-muted text-foreground border-0 " +
  "focus:outline-none focus:ring-2 focus:ring-muted-foreground/70 focus:ring-offset-2 focus:ring-offset-sheet " +
  "dark:[color-scheme:dark]"

// The count fields render through `Input`, which brings its own `h-10`.
// Cancel it so they keep the `py-3` height of the date fields beside them.
const nativeStyledNumericClass = cn(nativeInputClass, "h-auto")

interface PreorderEditSectionProps {
  listingId: string
  initial: {
    preorder_deadline?: string | null
    estimated_delivery?: string | null
    min_orders?: number | null
    max_orders?: number | null
  }
  onChange: (next: {
    preorder_deadline: string | null
    estimated_delivery: string | null
    min_orders: number | null
    max_orders: number | null
    isValid: boolean
  }) => void
}

export type PreorderEditStatus = "open" | "funded" | "in_production" | "shipped" | null

export interface PreorderLockState {
  loading: boolean
  locked: boolean
  paidCount: number
  status: PreorderEditStatus
}

export function usePreorderLockState(listingId: string | undefined, isPreorder: boolean): PreorderLockState {
  const [state, setState] = useState<PreorderLockState>({ loading: isPreorder, locked: false, paidCount: 0, status: null })

  useEffect(() => {
    if (!isPreorder || !listingId) {
      setState({ loading: false, locked: false, paidCount: 0, status: null })
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true }))
    ;(async () => {
      try {
        const res = await fetch(`/api/listings/${listingId}/preorder-state`)
        if (!res.ok) throw new Error("preorder-state failed")
        const json = (await res.json()) as { paid_count?: number; locked?: boolean; preorder_status?: PreorderEditStatus }
        if (cancelled) return
        setState({
          loading: false,
          locked: Boolean(json.locked),
          paidCount: typeof json.paid_count === "number" ? json.paid_count : 0,
          status: json.preorder_status ?? null,
        })
      } catch {
        if (!cancelled) setState({ loading: false, locked: false, paidCount: 0, status: null })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [listingId, isPreorder])

  return state
}

export function PreorderLockedBanner() {
  const t = useTranslations("preorder.edit")
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
      <div className="space-y-1">
        <p className="font-semibold">{t("lockedTitle")}</p>
        <p className="text-amber-200/80">{t("lockedBody")}</p>
      </div>
    </div>
  )
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return ""
  try {
    return new Date(value).toISOString().slice(0, 10)
  } catch {
    return ""
  }
}

export function PreorderEditSection({ initial, onChange }: PreorderEditSectionProps) {
  const t = useTranslations("preorder.edit")
  const tCreation = useTranslations("preorder.creation")
  const [deadline, setDeadline] = useState(toDateInput(initial.preorder_deadline))
  const [eta, setEta] = useState(toDateInput(initial.estimated_delivery))
  // Numbers, not draft strings — `NumericInput` owns the draft.
  const [minOrders, setMinOrders] = useState<number | undefined>(initial.min_orders ?? undefined)
  const [maxOrders, setMaxOrders] = useState<number | undefined>(initial.max_orders ?? undefined)
  const [error, setError] = useState<string>("")
  const countCodec = useMemo(() => integerCodec(), [])

  useEffect(() => {
    const deadlineIso = deadline ? new Date(deadline).toISOString() : null
    const etaIso = eta ? new Date(eta).toISOString() : null
    const minN = minOrders ?? null
    const maxN = maxOrders ?? null

    let err = ""
    if (deadlineIso && etaIso && new Date(etaIso) < new Date(deadlineIso)) {
      err = tCreation("etaBeforeDeadline")
    } else if (minN != null && maxN != null && maxN <= minN) {
      err = tCreation("maxLteMin")
    }
    setError(err)

    onChange({
      preorder_deadline: deadlineIso,
      estimated_delivery: etaIso,
      min_orders: minN,
      max_orders: maxN,
      isValid: !err,
    })
  }, [deadline, eta, minOrders, maxOrders, onChange, tCreation])

  return (
    <div className="space-y-3 rounded-2xl bg-sheet/60 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("fieldsTitle")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="po-deadline" className="mb-2 block text-sm text-foreground/80">{t("deadline")}</Label>
          <input
            id="po-deadline"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className={nativeInputClass}
          />
        </div>
        <div>
          <Label htmlFor="po-eta" className="mb-2 block text-sm text-foreground/80">{t("eta")}</Label>
          <input
            id="po-eta"
            type="date"
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            className={nativeInputClass}
          />
        </div>
        <div>
          <Label htmlFor="po-min" className="mb-2 block text-sm text-foreground/80">{t("minOrders")}</Label>
          <NumericInput
            id="po-min"
            codec={countCodec}
            value={minOrders}
            onValueChange={setMinOrders}
            className={nativeStyledNumericClass}
          />
        </div>
        <div>
          <Label htmlFor="po-max" className="mb-2 block text-sm text-foreground/80">{t("maxOrders")}</Label>
          <NumericInput
            id="po-max"
            codec={countCodec}
            value={maxOrders}
            onValueChange={setMaxOrders}
            className={nativeStyledNumericClass}
          />
        </div>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  )
}
